import { cn } from 'cn';
import { type PropsWithChildren, type ReactNode } from 'react';
import { HeadingRow } from '~/molecules/HeadingRow';

type P = PropsWithChildren<{
  readonly bordered?: boolean;
  readonly displayStyle?: 'native' | 'sectionWithHeading';
  readonly id?: string;
  readonly legend?: ReactNode;
  readonly padded?: boolean;
}> &
  ComponentStyling;

/**
 * Renders a fieldset component with optional border and padding.
 *
 * @param bordered - Whether to display a border.
 * @param children - The content to be rendered inside the fieldset.
 * @param className - Additional CSS classes to apply to the fieldset.
 * @param displayStyle - The display style of the fieldset. 'native' or 'sectionWithHeading'.
 * @param id - The id attribute for the fieldset.
 * @param legend - The legend to be displayed above the fieldset.
 * @param padded - Whether to add padding to the fieldset.
 * @param style - Additional inline styles to apply to the fieldset.
 * @param rest - Additional props to be spread onto the fieldset.
 * @return The rendered fieldset component.
 */
export const Fieldset = ({
  bordered = true,
  children,
  className = '',
  displayStyle = 'native',
  id,
  legend,
  padded = true,
  style,
  ...rest
}: P) =>
  displayStyle === 'sectionWithHeading' ? (
    <div
      className={cn(
        'relative flex w-full flex-col gap-4 rounded-md border border-tiga-border-lighter contain-paint',
        {
          'p-4': padded,
        },
        className,
      )}
      id={id}
      style={style}
      {...rest}
    >
      {legend ? (
        <HeadingRow
          heading={legend}
          variant="h4"
          className={cn('border-b border-tiga-border-lighter bg-white p-4', {
            '-mx-4 -mt-4': padded,
          })}
        />
      ) : null}
      {children}
    </div>
  ) : (
    <fieldset
      className={cn(
        'relative flex w-full flex-col gap-4 rounded-md',
        {
          'border border-tiga-border-lighter': bordered,
          'p-4': padded,
        },
        className,
      )}
      id={id}
      style={style}
      {...rest}
    >
      {legend ? (
        <legend
          className={cn('px-2 text-base', {
            '-mx-2 -mb-2': padded,
          })}
        >
          {legend}
        </legend>
      ) : null}
      {children}
    </fieldset>
  );
